/**
 * Slim cart utilities: variant handling, product form submission, cart page updates.
 * Drawer rendering is handled by React.
 */

// Runs fn in a task after the next frame is painted. Google tag, the Meta pixel and Shopify's analytics react
// synchronously to address-bar changes and to form change events, so a variant pick waited for them before the new
// variant showed (10-26: 250 -> 170 ms on a mobile Chrome emulation, 245 -> 135 ms in Safari's engine).
function tvAfterNextPaint(fn) {
  requestAnimationFrame(() => setTimeout(fn, 0));
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.updateSelectedSwatchValue(event);
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      // A combination with no variant must not submit the previously selected one.
      this.toggleAddButton(true, window.variantStrings.unavailable, true);
      this.setUnavailable();
      this.clearVariantInput();
    } else {
      this.toggleAddButton(false, '', false);
      this.updateMedia();
      tvAfterNextPaint(() => this.updateURL());
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value;
      }
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateSelectedSwatchValue({ target }) {
    const { name, value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const selectedSwatchValue = this.querySelector(`[data-opt="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = this.querySelector(`[data-opt="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    const currentMedia = $('.primgSlider .swiper-slide-active').data('id');
    if (currentMedia !== `media${this.currentVariant.featured_media.id}` || Shopify.designMode) {
      mediaGalleries.forEach((mediaGallery) =>
        mediaGallery.setActiveMedia(`media${this.currentVariant.featured_media.id}`, true)
      );
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      tvAfterNextPaint(() => input.dispatchEvent(new Event('change', { bubbles: true })));
    });
  }

  clearVariantInput() {
    document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) input.value = '';
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(elementList, availableValuesList) {
    elementList.forEach((element) => {
      const value = element.getAttribute('value');
      const availableElement = availableValuesList.includes(value);

      if (element.tagName === 'INPUT') {
        element.classList.toggle('disabled', !availableElement);
      } else if (element.tagName === 'OPTION') {
        element.classList.toggle('disabled', !availableElement);
      }
    });
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
        const volumePricingSource = html.getElementById(
          `Volume-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(
          `Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        if (qtyRules) qtyRules.classList.remove('hidden');

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) price.classList.remove('hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true, window.variantStrings.soldOut);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = false, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      const variant = this.currentVariant;
      const qty = variant ? $(".prvQty").data(`v${variant.id}`) : undefined;
      if (variant && qty < 1 && variant.inventory_management === 'shopify') {
        addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    }
    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote) volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules) qtyRules.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('variant-selects', VariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (event.data.sectionId !== sectionId) return;
          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;
        this.input.max = max;
        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;
        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }
    }
  );
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".qtyBtn[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".qtyBtn[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      e.preventDefault();
      const cartItems = this.closest('cart-items');
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.index, 0, undefined, undefined, this.dataset.key);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    // One debounce per quantity field: editing a second line inside the window must not drop the first line's edit.
    this.pendingChanges = new Map();
    // Writes go out one after another; each is addressed by line key, so a line removed before it cannot shift it.
    this.writeQueue = Promise.resolve();
    this.renderedSignature = null;
    this.addEventListener('change', (event) => this.scheduleChange(event));
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible') this.onCartUpdate();
    };
    this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    // The drawer, another tab or a native add can change the cart while this page shows it; re-render from the server.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate(event && event.cartData);
    });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.cartForm = this.querySelector('form#cart');
    if (this.cartForm) this.cartForm.addEventListener('submit', this.onCheckoutSubmit);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.cartForm) this.cartForm.removeEventListener('submit', this.onCheckoutSubmit);
  }

  scheduleChange(event) {
    const input = event.target;
    if (!input || !input.dataset || !input.dataset.index) return;
    const field = input.id || input.dataset.index;
    clearTimeout(this.pendingChanges.get(field));
    this.pendingChanges.set(field, setTimeout(() => {
      this.pendingChanges.delete(field);
      this.onChange(event);
    }, 300));
  }

  // Checkout leaves with the cart Shopify holds: quantity fields are sent only through change.js, never as the
  // form's positional updates[], which would rewrite the cart from this page's possibly stale copy.
  onCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    event.preventDefault();
    const form = this.cartForm;
    this.pendingChanges.forEach((timer, field) => {
      clearTimeout(timer);
      const input = document.getElementById(field) || this.querySelector(`[data-index="${field}"]`);
      if (input) this.onChange({ target: input });
    });
    this.pendingChanges.clear();
    this.writeQueue
      // A write that failed rendered nothing: render the cart Shopify holds before deciding.
      .then(() => (this.renderStale ? this.renderHeldCart() : undefined))
      .then(() => {
        // Decided again on the settled cart: a write that took the cart below the minimum order rendered the blocked
        // button, and checkout then opens the minimum order modal instead of leaving.
        const blocked = this.querySelector('[data-tv-min-order-blocked]') || document.querySelector('#main-cart-footer [data-tv-min-order-blocked]');
        if (blocked) { blocked.click(); return; }
        form.querySelectorAll('[name="updates[]"]').forEach((input) => { input.disabled = true; });
        let checkout = form.querySelector('input[type="hidden"][name="checkout"]');
        if (!checkout) {
          checkout = document.createElement('input');
          checkout.type = 'hidden';
          checkout.name = 'checkout';
          form.appendChild(checkout);
        }
        checkout.value = submitter.value || '';
        HTMLFormElement.prototype.submit.call(form);
      });
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement && document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      event.target.dataset.key
    );
  }

  cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.map((item) => `${item.key}:${item.quantity}`).join('|') + `#${cart.total_price}`;
  }

  onCartUpdate(cartData) {
    const signature = this.cartSignature(cartData);
    if (signature && signature === this.renderedSignature) return;
    const sections = this.getSectionsToRender();
    this.writeQueue = this.writeQueue
      .then(() => fetch(`${routes.cart_url}?sections=${[...new Set(sections.map((section) => section.section))].join(',')}`))
      .then((response) => response.json())
      .then((rendered) => {
        this.renderSections(rendered);
        return fetch(`${routes.cart_url}.js`).then((response) => response.json());
      })
      .then((cart) => {
        this.renderedSignature = this.cartSignature(cart);
        this.toggleEmpty(cart.item_count);
        if (typeof freeShippMsg === 'function') freeShippMsg();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderHeldCart() {
    const sections = [...new Set(this.getSectionsToRender().map((section) => section.section))];
    return fetch(`${routes.cart_url}?sections=${sections.join(',')}`)
      .then((response) => response.json())
      .then((rendered) => { this.renderSections(rendered); this.renderStale = false; })
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.cartTotal',
      },
      {
        // The checkout button turns into "still missing" below the minimum order and back again.
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.cart__ctas',
      }
    ];
  }

  renderSections(rendered) {
    if (!rendered) return;
    this.getSectionsToRender().forEach((section) => {
      const html = rendered[section.section];
      const container = document.getElementById(section.id);
      if (!html || !container) return;
      const elementToReplace = container.querySelector(section.selector) || container;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (source) elementToReplace.innerHTML = source.innerHTML;
    });
  }

  toggleEmpty(itemCount) {
    this.classList.toggle('is-empty', itemCount === 0);
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
  }

  updateQuantity(line, quantity, name, variantId, key) {
    const write = () => this.writeQuantity(line, quantity, name, variantId, key);
    this.writeQueue = this.writeQueue.then(write, write);
    return this.writeQueue;
  }

  writeQuantity(line, quantity, name, variantId, key) {
    this.enableLoading(line);

    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity,
      sections: [...new Set(this.getSectionsToRender().map((section) => section.section))],
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}?${LIQUID_CART_WRITE_SOURCE}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.toggleEmpty(parsedState.item_count);
        this.renderSections(parsedState.sections);
        this.renderedSignature = this.cartSignature(parsedState);

        const updatedItem = key
          ? parsedState.items.find((item) => item.key === key)
          : parsedState.items[line - 1];
        const updatedValue = updatedItem ? updatedItem.quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (document.querySelector('.cart-item')) {
          document.querySelector('.cart-item__name')?.focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        requestReactCartRefresh('liquid:cart-change');
      })
      .catch(() => {
        this.renderStale = true;
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    // Shopify returns errors as a string or as a field map; show the first readable text.
    const text = typeof message === 'string'
      ? message
      : (message && Object.values(message).flat().find((value) => typeof value === 'string')) || window.cartStrings.error;
    const lineItemError = document.getElementById(`Line-item-error-${line}`);
    const lineItemErrorText = lineItemError && lineItemError.querySelector('.ctItem-error');
    // An empty text also hides the icon (.ctItem-error:empty + svg).
    if (lineItemErrorText) lineItemErrorText.textContent = text;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.textContent = text;
      cartStatus.setAttribute('aria-hidden', false);
    }

    if (typeof freeShippMsg === 'function') freeShippMsg();

    setTimeout(() => {
      if (cartStatus) cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    cartItemElements.forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('input', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}?${LIQUID_CART_WRITE_SOURCE}`, { ...fetchConfig(), ...{ body } })
          .then((response) => { if (response.ok) requestReactCartRefresh('liquid:cart-note'); })
          .catch(() => {});
      }, 300));
    }
  });
}

// Parser upgrades, shallow clones and staged innerHTML can connect a custom element before its
// children exist. Retry setup on child mutations instead of reading children in the constructor.
function initWhenChildrenReady(host) {
  if (host.initialize()) {
    stopWaitingForChildren(host);
    return;
  }
  if (host.childObserver || typeof MutationObserver !== 'function') return;
  host.childObserver = new MutationObserver(() => {
    if (host.initialize()) stopWaitingForChildren(host);
  });
  host.childObserver.observe(host, { childList: true, subtree: true });
}

function stopWaitingForChildren(host) {
  if (!host.childObserver) return;
  host.childObserver.disconnect();
  host.childObserver = null;
}

function readStoredDiscount() {
  try {
    return localStorage.getItem('storedDiscount');
  } catch (error) {
    return null;
  }
}

class CouponCode extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.input = null;
    this.button = null;
    this.status = null;
    this.childObserver = null;
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    initWhenChildrenReady(this);
  }

  disconnectedCallback() {
    stopWaitingForChildren(this);
  }

  initialize() {
    const form = this.querySelector('[data-coupon-form]');
    const input = this.querySelector('input[name="discount"]');
    if (!form || !input) return false;
    if (form === this.form) return true;

    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    this.form = form;
    this.input = input;
    this.button = this.querySelector('[data-coupon-submit]');
    this.status = this.querySelector('[data-coupon-status]');
    const storedDiscount = readStoredDiscount();
    if (storedDiscount) input.value = storedDiscount;
    // The button submits, and so does Enter in the field (native form submission).
    form.addEventListener('submit', this.onSubmit);
    return true;
  }

  showStatus(kind, code) {
    if (!this.status) return;
    const message = (this.dataset[`message${kind}`] || '').replace('[code]', code || '');
    this.status.textContent = message;
    this.status.hidden = !message;
    this.status.dataset.state = kind.toLowerCase();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.pending) return;
    const code = this.input.value.trim();
    if (!code) {
      this.showStatus('Empty');
      this.input.focus();
      return;
    }
    this.pending = true;
    if (this.button) this.button.disabled = true;
    try {
      // The Ajax Cart API applies the code to the cart and answers whether it applies (discount_codes[].applicable).
      // It replaces any code already on the cart; automatic discounts are untouched.
      const response = await fetch(`${routes.cart_update_url}?${LIQUID_CART_WRITE_SOURCE}`, { ...fetchConfig(), body: JSON.stringify({ discount: code }) });
      if (!response.ok) throw new Error(`cart update ${response.status}`);
      const cart = await response.json();
      const entry = (cart.discount_codes || []).find((item) => String(item.code).toLowerCase() === code.toLowerCase());
      try {
        localStorage.setItem('storedDiscount', code);
      } catch (error) {
        // Storage can be unavailable (private mode, blocked site data); the code is applied anyway.
      }
      requestReactCartRefresh('liquid:discount');
      if (entry && entry.applicable) {
        this.showStatus('Applied', entry.code);
        refreshCartTotals();
      } else {
        this.showStatus('Invalid', code);
      }
    } catch (error) {
      this.showStatus('Error');
    } finally {
      this.pending = false;
      if (this.button) this.button.disabled = false;
    }
  }
}

// Re-renders the /cart totals (discount lines, savings, total) from the section, leaving the rest of the footer
// (the coupon field and its message) in place.
function refreshCartTotals() {
  const footer = document.getElementById('main-cart-footer');
  const sectionId = footer?.dataset.id;
  if (!sectionId) return;
  fetch(`${routes.cart_url}?section_id=${sectionId}`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const next = html.querySelectorAll('#main-cart-footer .cartTotal');
      document.querySelectorAll('#main-cart-footer .cartTotal').forEach((current, index) => {
        if (next[index]) current.innerHTML = next[index].innerHTML;
      });
    })
    .catch(() => {});
}
customElements.define('coupon-code', CouponCode);

function startTimer(duration, display) {
  let timer = duration;
  let minutes;
  let seconds;
  setInterval(() => {
    minutes = parseInt(timer / 60, 10);
    seconds = parseInt(timer % 60, 10);

    minutes = minutes < 10 ? `0${minutes}` : minutes;
    seconds = seconds < 10 ? `0${seconds}` : seconds;

    display.textContent = `${minutes}:${seconds}`;

    if (--timer < 0) {
      clearInterval(startTimer);
      document.querySelector('.cartCountdown')?.remove();
    }
  }, 1000);
}

window.onload = function () {
  if (document.querySelector('.cartCountdown')) {
    const minutes = document.querySelector('.cartCountdown').getAttribute('data-countdown');
    const display = document.querySelector('#cartTime');
    startTimer(minutes, display);
  }
};

// Liquid wrote the cart outside the React cart (line quantity, note, discount): ask React to re-read it, so the
// drawer, counter and recommendations never show the cart from before this write. Those writes carry this marker so
// React's fetch watcher, which re-reads after unannounced cart writes (other apps), does not read a second time.
var LIQUID_CART_WRITE_SOURCE = '_source=liquid';

function requestReactCartRefresh(reason) {
  if (typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent('tv:cart-written', { detail: { reason } }));
}

function emitCartToast(detail) {
  if (typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent('cart:toast', { detail }));
}

function resolveErrorMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload;
  return payload.description || payload.message || fallback;
}

function buildCartAddErrorToastDetail(result) {
  if (result && result.errorCode === 'SOLD_OUT') {
    return { type: 'error', code: 'SOLD_OUT' };
  }
  if (result && result.errorCode === 'STOCK_LIMIT') {
    const maxQty = Number(result.stockLimitMaxQty);
    return {
      type: 'error',
      code: 'STOCK_LIMIT',
      context: Number.isFinite(maxQty) ? { maxQty } : undefined,
    };
  }
  return {
    type: 'error',
    messageKey: 'addToCartFailed',
  };
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.submitButton = null;
      this.childObserver = null;
      this.keepDisabled = false;
      this.onSubmitHandler = this.onSubmitHandler.bind(this);
    }

    connectedCallback() {
      initWhenChildrenReady(this);
    }

    disconnectedCallback() {
      stopWaitingForChildren(this);
    }

    initialize() {
      const form = this.querySelector('form');
      const variantInput = form && form.querySelector('[name=id]');
      if (!variantInput) return false;
      if (form === this.form) return true;

      // Rebind when the form was re-rendered; the instance field (not a data-* marker that DOM
      // clones would copy) is what guarantees a single submit listener per form.
      if (this.form) this.form.removeEventListener('submit', this.onSubmitHandler);
      this.form = form;
      this.errorMessageWrapper = null;
      this.errorMessage = null;
      variantInput.disabled = false;
      form.addEventListener('submit', this.onSubmitHandler);
      this.submitButton = this.querySelector('[type="submit"]');
      this.hideErrors = this.dataset.hideErrors === 'true';
      return true;
    }

    setSubmittingState(isSubmitting) {
      if (!this.submitButton) return;
      const spinner = this.querySelector('.loading__spinner');
      if (isSubmitting) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
      } else {
        this.submitButton.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        if (!this.keepDisabled) this.submitButton.removeAttribute('aria-disabled');
      }
    }

    clearOverlay() {
      document.body.classList.remove('loading');
      document.body.classList.remove('showOverly');
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.keepDisabled = false;
      this.setSubmittingState(true);

      try {
        const helper = window.__FIRST_PARTY_CART_ADD__;
        if (!helper || typeof helper.request !== 'function') {
          // The add helper never loaded: let Shopify handle the form natively (single write, redirect to cart).
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }
        // A product section's form (it carries the section id) opens the after-add panel; a card's form gets a toast.
        const result = await helper.request({ form: this.form, source: this.dataset.sectionId ? 'product-form' : 'product-card' });

        if (!result.ok) {
          if (helper.reportUnconfirmed(result)) return;
          if (result.transport === 'native') {
            // No App consumer is guaranteed on the native path: show Shopify's own message inline.
            this.handleErrorMessage(result.errorMessage || (window.cartStrings && window.cartStrings.error));
          } else {
            emitCartToast(buildCartAddErrorToastDetail(result));
          }

          const soldOutMessage = this.submitButton && this.submitButton.querySelector('.sold-out-message');
          if (result.errorCode === 'SOLD_OUT' && soldOutMessage) {
            this.keepDisabled = true;
            this.submitButton.setAttribute('aria-disabled', true);
            const label = this.submitButton.querySelector('span');
            if (label) label.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          }
          return;
        }

        this.error = false;
        if (result.transport === 'native') {
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal && typeof quickAddModal.hide === 'function') quickAddModal.hide(true);
          helper.presentNativeSuccess('product-form');
        }

      } catch (error) {
        console.error(error);
        const message = (window.cartStrings && window.cartStrings.error) || 'Cart error';
        this.handleErrorMessage(message);
      } finally {
        this.setSubmittingState(false);
        this.clearOverlay();
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper =
        this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
